import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import matterService from '@api/v1/matterService'
import DataGrid from '@components/common/DataGrid'
import MatterForm from '@components/matters/MatterForm'
import ConfirmModal from '@components/common/ConfirmModal'
import { toast } from '@store/toastStore'
import useKeyboardShortcuts from '@hooks/useKeyboardShortcuts'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const Matters = () => {
  useDocumentTitle('Matters')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeStatus, setActiveStatus] = useState(1)
  const [showCreate, setShowCreate] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState(null)
  const [bulkArchiveRows, setBulkArchiveRows] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])

  const shortcuts = useMemo(() => [
    { key: 'n', meta: true, handler: () => setShowCreate(true) },
  ], [])
  useKeyboardShortcuts(shortcuts)

  const listQuery = useQuery({
    queryKey: ['matters', activeStatus],
    queryFn: () => matterService.getMattersEnriched({
      activeStatus, limit: 1000, offset: 0,
    }),
  })

  const searchQuery = useQuery({
    queryKey: ['matterSearch', searchTerm],
    queryFn: () => matterService.searchMatters(searchTerm),
    enabled: searchTerm.length >= 2,
  })

  const createMutation = useMutation({
    mutationFn: (matterData) => matterService.createMatter(matterData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matters'] })
      setShowCreate(false)
      toast.success('Matter created successfully')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to create matter')
    },
  })

  const archiveMutation = useMutation({
    mutationFn: (matterId) => matterService.archiveMatter(matterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matters'] })
      setArchiveTarget(null)
      toast.success('Matter archived')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to archive matter')
    },
  })

  const bulkArchiveMutation = useMutation({
    mutationFn: async (rows) => {
      for (const row of rows) {
        const id = row.id || row.matterId || row.objectId
        if (id) await matterService.archiveMatter(id)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matters'] })
      setBulkArchiveRows(null)
      setSelectedRows([])
      toast.success('Matters archived')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to archive matters')
    },
  })

  const isSearching = searchTerm.length >= 2

  /*
   * CC-53132 — three outcomes, not two. See `@api/v1/readState`.
   *
   * This page used to read `isLoading`, which react-query defines as
   * `isPending && isFetching` — so it is FALSE for a query that is pending but
   * NOT in flight. The one that bites is `fetchStatus: 'paused'`: under the
   * default `networkMode: 'online'`, react-query refuses to fire while
   * `onlineManager` believes the browser is offline. `queryFn` is never called,
   * nothing throws, nothing logs, and `data` stays `undefined`.
   *
   * `data?.matters || []` and `data?.total || 0` then turned that non-answer
   * into two confident statements: an empty grid reading "No matters found" and
   * a subtitle reading "0 matters". For a firm whose matter list IS its book of
   * business, "you have no matters" is a wrong number, and it is unfalsifiable
   * from the outside — the failing load makes NO request, so there is nothing to
   * find in any log on either side.
   *
   * The state is read off whichever query is actually driving the screen. The
   * old code only ever surfaced `listQuery.error`, so a FAILED search read fell
   * through to `[]` and the grid said "No matters found" for a search that had
   * errored — the same lie by a different route.
   */
  const activeQuery = isSearching ? searchQuery : listQuery
  const mattersState = useReadState(isSearching ? 'matters-search' : 'matters-list', activeQuery)
  const readReady = mattersState === 'ready'

  /*
   * Deliberately NOT `|| []`: that idiom is the defect. `undefined` has to reach
   * `DataGrid`, which distinguishes it from `[]` and renders its honest
   * "could not be loaded, so it is not known to be empty" alert instead of
   * `emptyMessage` — and renders no row count.
   */
  const matters = activeQuery.data?.matters
  // A count is a claim. It may only be made about a read that finished; until
  // then the page says nothing rather than "0 matters". `|| 0` is unchanged and
  // still applies once the read IS resolved (a 200 that omits `total`).
  const total = readReady ? (activeQuery.data?.total || 0) : null

  function parseName(name) {
    if (!name) return { desc: '', client: '' }
    const parts = name.split(' - ')
    if (parts.length >= 3) {
      return {
        desc: parts.slice(0, -2).join(' - '),
        client: parts[parts.length - 1].trim(),
      }
    }
    if (parts.length === 2) return { desc: parts[0].trim(), client: '' }
    return { desc: name, client: '' }
  }

  const columns = useMemo(() => [
    {
      id: 'matterName',
      header: 'Matter Name',
      accessorFn: (row) =>
        row.matterName || row.name || parseName(row.fullName).desc || 'Unnamed',
      cell: ({ getValue }) => (
        <span className="font-medium text-cb-accent-text">{getValue()}</span>
      ),
      size: 220,
    },
    { id: 'attorney', header: 'Attorney', accessorKey: 'attorney', size: 150 },
    {
      id: 'number', header: 'Matter Number',
      accessorKey: 'number', size: 130,
    },
    {
      id: 'clientName', header: 'Client',
      accessorFn: (row) =>
        row.clientName || parseName(row.fullName).client || '',
      size: 150,
    },
    {
      id: 'openDate', header: 'Open Date', accessorKey: 'openDate',
      cell: ({ getValue }) => {
        const v = getValue()
        return v ? dayjs(v).format('M/D/YYYY') : ''
      },
      size: 110,
    },
    {
      id: 'practiceArea', header: 'Practice Area',
      accessorKey: 'practiceArea', size: 130,
    },
    {
      id: 'feeArrangement', header: 'Fee Arrangement',
      accessorKey: 'feeArrangement', size: 130,
    },
    {
      id: 'status', header: 'Status',
      accessorFn: (row) => row.isActive !== false ? 'Active' : 'Inactive',
      cell: ({ getValue }) => {
        const v = getValue()
        return (
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            v === 'Active'
              ? 'text-cb-teal-dark bg-cb-teal/10'
              : 'text-gray-500 bg-gray-100'
          }`}>
            {v}
          </span>
        )
      },
      size: 90,
    },
  ], [])

  const handleRowClick = (row) => {
    const matterId = row.id || row.matterId || row.objectId
    if (matterId) navigate(`/matters/${matterId}`)
  }

  const bulkActions = useMemo(() => [
    {
      label: 'Archive Selected',
      variant: 'danger',
      isLoading: bulkArchiveMutation.isPending,
      onClick: (rows) => setBulkArchiveRows(rows),
    },
  ], [bulkArchiveMutation.isPending])

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Matters</h1>
          {/* CC-53132: rendered only for a resolved read. A "0 matters" beside a
              read that never happened is a claim, not an observation, and this
              subtitle is the most quotable number on the page. */}
          {readReady && (
            <p className="text-sm text-gray-500 mt-1">
              {total} matter{total !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2.5 bg-cb-accent text-white rounded-lg text-sm
            font-medium hover:bg-cb-accent/90 transition-colors shadow-sm"
        >
          + New Matter
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center
          justify-center z-50 p-4"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full
            max-h-[90vh] overflow-y-auto p-6"
          >
            <h2 className="text-lg font-display text-gray-900 mb-4">
              New Matter
            </h2>
            {createMutation.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200
                rounded-lg text-sm text-red-600"
              >
                {createMutation.error.message || 'Failed to create matter'}
              </div>
            )}
            <MatterForm
              onSave={(formData) => createMutation.mutate(formData)}
              onCancel={() => setShowCreate(false)}
              saving={createMutation.isPending}
            />
          </div>
        </div>
      )}

      {/* Archive Confirm */}
      {archiveTarget && (
        <ConfirmModal
          title="Archive Matter"
          message={`Are you sure you want to archive "${archiveTarget.name || 'this matter'}"? It can be restored later.`}
          confirmLabel="Archive"
          variant="danger"
          isLoading={archiveMutation.isPending}
          onConfirm={() => archiveMutation.mutate(archiveTarget.id || archiveTarget.matterId || archiveTarget.objectId)}
          onCancel={() => setArchiveTarget(null)}
        />
      )}

      {/* Bulk Archive Confirm */}
      {bulkArchiveRows && (
        <ConfirmModal
          title="Archive Matters"
          message={`Are you sure you want to archive ${bulkArchiveRows.length} matter(s)? They can be restored later.`}
          confirmLabel="Archive All"
          variant="danger"
          isLoading={bulkArchiveMutation.isPending}
          onConfirm={() => bulkArchiveMutation.mutate(bulkArchiveRows)}
          onCancel={() => setBulkArchiveRows(null)}
        />
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 max-w-md relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4
              text-gray-500"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round" strokeLinejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5
                7.5 0 0010.607 10.607z"
            />
          </svg>
          <input
            type="text"
            aria-label="Search matters"
            placeholder="Search matters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border
              border-gray-200 rounded-lg text-sm text-gray-900
              placeholder:text-gray-500 focus:outline-none focus:ring-2
              focus:ring-cb-accent/30 focus:border-cb-accent"
          />
        </div>
        <select
          aria-label="Filter by status"
          value={activeStatus}
          onChange={(e) => setActiveStatus(Number(e.target.value))}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg
            text-sm text-gray-700 focus:outline-none focus:ring-2
            focus:ring-cb-accent/30 focus:border-cb-accent"
        >
          <option value={1}>Active</option>
          <option value={2}>Inactive</option>
          <option value={3}>All</option>
        </select>
      </div>

      {/* Error state — a read that FAILED, which is a different fact from a firm
          with no matters. It gets `role="alert"` and a retry, because a user who
          is shown the empty state instead has no reason to try again: they
          conclude the matters are gone. */}
      {mattersState === 'failed' ? (
        <div
          role="alert"
          className="bg-white rounded-xl shadow-sm border border-gray-100
            p-12 text-center"
        >
          <p className="text-sm text-red-500">
            Failed to load matters: {activeQuery.error?.message}
          </p>
          <p className="mt-2 text-sm text-gray-500">
            This is not an empty list — your matters could not be read at all.
          </p>
          <button
            type="button"
            onClick={() => activeQuery.refetch()}
            className="mt-3 rounded-lg border border-rc-control bg-white px-3
              py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
          >
            Try again
          </button>
        </div>
      ) : mattersState === 'blocked' ? (
        /* The state that used to render as "No matters found" with "0 matters"
           above it. react-query PAUSED the fetch, so there is no request, no
           error and — because `isLoading` is false here — not even a spinner.
           Say what is actually happening and offer the way out. */
        <div
          role="alert"
          className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm
            text-amber-900"
        >
          <p className="font-medium">
            Can’t reach the server, so we haven’t loaded your matters.
          </p>
          <p className="mt-1 text-amber-800">
            This is not an empty list — your connection looks offline. Check it
            and try again.
          </p>
          <button
            type="button"
            onClick={() => activeQuery.refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3
              py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      ) : (
        /* `loading`, `idle` and `ready` all land here.
           `idle` rides with `loading` ON PURPOSE, and it is unreachable today:
           `idle` means pending-but-never-started, i.e. `enabled: false`, and the
           only conditionally-enabled query on this page is the search — whose
           `enabled` predicate (`searchTerm.length >= 2`) is the SAME expression
           as `isSearching`, which is what selects it as `activeQuery`. So the
           active read is never a disabled one, and folding idle in cannot strand
           the page on a permanent spinner. It is covered anyway because every
           other branch is gated on an explicit state: an unhandled fifth state
           would render a BLANK PAGE, which is how the first version of this fix
           broke the report catalog. */
        <DataGrid
          columns={columns}
          data={matters}
          isLoading={mattersState === 'loading' || mattersState === 'idle'}
          emptyMessage="No matters found"
          unresolvedMessage="These matters could not be loaded, so this is not known to be an empty list. Reload to try again."
          loadingMessage="Loading matters..."
          onRowClick={handleRowClick}
          storageKey="matters"
          enableExport={true}
          enableFiltering={true}
          enableSelection={true}
          onSelectionChange={setSelectedRows}
          bulkActions={bulkActions}
          exportFilename="matters"
        />
      )}
    </div>
  )
}

export default Matters
